import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, message, DatePicker } from 'antd';
import { makePostCall, calculateExactAge } from '../utils/helper';
import dayjs from 'dayjs';
import { FieldLabels } from '../constants/fieldLabels';

const { Option } = Select;
const { TextArea } = Input;

interface EditStudyDetailsModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  studyData: {
    patientName: string;
    patientId: string;
    patientAge: string | number;
    patientDOB?: string;
    patientGender: string;
    studyDescription: string;
    accessionNumber: string;
    referringPhysician: string;
    history: string;
    studyUID?: string;
    orderId?: string;
  };
}

const EditStudyDetailsModal: React.FC<EditStudyDetailsModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  studyData
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialValues, setInitialValues] = useState<any>({});

  useEffect(() => {
    if (visible && studyData) {
      // Calculate age from DOB if available, otherwise use the provided age
      const calculatedAge = studyData.patientDOB
        ? calculateExactAge(studyData.patientDOB)
        : null;
      const finalAge = calculatedAge || studyData.patientAge || '';

      // Parse DOB from YYYYMMDD format to dayjs object for DatePicker
      const dobDayjs = studyData.patientDOB && dayjs(studyData.patientDOB, 'YYYYMMDD').isValid()
        ? dayjs(studyData.patientDOB, 'YYYYMMDD')
        : null;

      const values = {
        patientName: studyData.patientName || '',
        patientId: studyData.patientId || '',
        patientAge: finalAge,
        patientGender: studyData.patientGender || '',
        studyDescription: studyData.studyDescription || '',
        accessionNumber: studyData.accessionNumber || '',
        referringPhysician: studyData.referringPhysician || '',
        history: studyData.history || '',
        patientDOB: dobDayjs
      };

      // Store initial values including DOB for comparison
      setInitialValues({
        ...values,
      });
      form.setFieldsValue(values);

      setHasChanges(false);
    }
  }, [visible, studyData, form]);

  const checkForChanges = () => {
    const currentValues = form.getFieldsValue();
    const isChanged = Object.keys(initialValues).some(key => {
      const initialValue = initialValues[key];
      const currentValue = currentValues[key];

      // Special handling for dayjs objects (DOB field)
      if (key === 'patientDOB') {
        if (!initialValue && !currentValue) return false;
        if (!initialValue || !currentValue) return true;
        if (dayjs.isDayjs(initialValue) && dayjs.isDayjs(currentValue)) {
          return !initialValue.isSame(currentValue, 'day');
        }
        return true;
      }

      // Regular comparison for other fields
      return initialValue !== currentValue;
    });
    setHasChanges(isChanged);
  };

  const handleDOBChange = (date: any) => {
    if (date && dayjs.isDayjs(date)) {
      const dobFormatted = date.format('YYYYMMDD');
      const calculatedAge = calculateExactAge(dobFormatted);
      form.setFieldValue('patientAge', calculatedAge || '');
    } else {
      form.setFieldValue('patientAge', '');
    }
    // Check for changes after updating age
    setTimeout(checkForChanges, 10);
  };

  const getChangedFields = () => {
    const currentValues = form.getFieldsValue();
    const fieldLabels: { [key: string]: string } = {
      patientName: FieldLabels.PATIENT_NAME,
      patientId: FieldLabels.PATIENT_ID,
      patientDOB: FieldLabels.PATIENT_DOB,
      patientAge: FieldLabels.PATIENT_AGE,
      patientGender: FieldLabels.PATIENT_SEX,
      studyDescription: FieldLabels.STUDY_DESCRIPTION,
      accessionNumber: FieldLabels.ACCESSION_NUMBER,
      referringPhysician: FieldLabels.REFERRING_PHYSICIAN,
      history: FieldLabels.HISTORY,
    };

    const changedFieldsData: { [key: string]: { old_value: any; new_value: any } } = {};

    Object.keys(initialValues).forEach(key => {
      const initialValue = initialValues[key];
      const currentValue = currentValues[key];

      // Special handling for dayjs objects (DOB field)
      if (key === 'patientDOB') {
        if (!initialValue && !currentValue) return;

        const oldDOB =
          initialValue && dayjs.isDayjs(initialValue) ? initialValue.format('DD-MM-YYYY') : null;
        const newDOB =
          currentValue && dayjs.isDayjs(currentValue) ? currentValue.format('DD-MM-YYYY') : null;

        if (
          !initialValue ||
          !currentValue ||
          (dayjs.isDayjs(initialValue) &&
            dayjs.isDayjs(currentValue) &&
            !initialValue.isSame(currentValue, 'day'))
        ) {
          changedFieldsData[fieldLabels[key]] = {
            old_value: oldDOB,
            new_value: newDOB,
          };
        }
        return;
      }

      // Regular comparison for other fields
      if (initialValue !== currentValue) {
        changedFieldsData[fieldLabels[key]] = {
          old_value: initialValue || null,
          new_value: currentValue || null,
        };
      }
    });

    return changedFieldsData;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Convert DOB moment object to YYYYMMDD format
      const dobFormatted = values.patientDOB ? values.patientDOB.format('YYYYMMDD') : null;

      // Get changed fields with old and new values
      const changedFieldsData = getChangedFields();

      const payload = {
        study_uid: studyData.studyUID,
        order_id: studyData.orderId,
        patient_name: values.patientName,
        patient_id: values.patientId,
        patient_dob: dobFormatted,
        patient_age: values.patientAge,
        patient_gender: values.patientGender,
        study_description: values.studyDescription,
        accession_number: values.accessionNumber,
        referring_physician: values.referringPhysician,
        history: values.history,
        updated_fields: changedFieldsData,
      };

      const response = await makePostCall('/update-study-details', payload);

      if (response.data.success) {
        message.success('Study details updated successfully');
        form.resetFields();
        setHasChanges(false);
        onSuccess?.();
        onCancel();
      } else {
        message.error(response.data.message || 'Failed to update study details');
      }
    } catch (error: any) {
      if (error.errorFields) {
        message.error('Please fill in all required fields');
      } else {
        message.error('Failed to update study details. Please try again.');
        console.error('Error updating study details:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setHasChanges(false);
    onCancel();
  };

  return (
    <Modal
      title="DICOM Edit"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={700}
      destroyOnClose
    >
      <div
        style={{
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: '#e6f7ff',
          borderRadius: '4px',
          border: '1px solid #91d5ff',
        }}
      >
        <strong>Note:</strong> Updated patient details will appear in the report
      </div>

      <Form
        form={form}
        layout="vertical"
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <Form.Item
            label={FieldLabels.PATIENT_NAME}
            name="patientName"
            style={{ marginBottom: 8 }}
            rules={[{ required: true, message: 'Please enter patient name' }]}
          >
            <Input
              placeholder="Enter patient name"
              onChange={checkForChanges}
            />
          </Form.Item>

          <Form.Item
            label={FieldLabels.PATIENT_ID}
            name="patientId"
            style={{ marginBottom: 8 }}
          >
            <Input
              placeholder="Enter patient ID"
              disabled
            />
          </Form.Item>

          <Form.Item
            label={FieldLabels.STUDY_DATE}
            name="patientDOB"
            style={{ marginBottom: 8 }}
          >
            <DatePicker
              format="DD-MM-YYYY"
              placeholder="Select study date"
              style={{ width: '100%' }}
              disabledDate={current => current && current > dayjs().endOf('day')}
              onChange={handleDOBChange}
            />
          </Form.Item>

          <Form.Item
            label={FieldLabels.PATIENT_AGE}
            name="patientAge"
            style={{ marginBottom: 8 }}
          >
            <Input
              placeholder="Enter patient age"
              disabled
            />
          </Form.Item>

          <Form.Item
            label={FieldLabels.PATIENT_SEX}
            name="patientGender"
            style={{ marginBottom: 8 }}
            rules={[{ required: true, message: 'Please select patient gender' }]}
          >
            <Select
              placeholder="Select gender"
              onChange={checkForChanges}
            >
              <Option value="M">Male</Option>
              <Option value="F">Female</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={FieldLabels.STUDY_DESCRIPTION}
            name="studyDescription"
            style={{ marginBottom: 8 }}
            rules={[{ required: true, message: 'Please enter study description' }]}
          >
            <Input
              placeholder="Enter study description"
              onChange={checkForChanges}
            />
          </Form.Item>

          <Form.Item
            label={FieldLabels.ACCESSION_NUMBER}
            name="accessionNumber"
            style={{ marginBottom: 8 }}
          >
            <Input
              placeholder="Enter accession number"
              disabled
            />
          </Form.Item>

          <Form.Item
            label={FieldLabels.REFERRING_PHYSICIAN}
            name="referringPhysician"
            style={{ marginBottom: 8 }}
            rules={[{ required: true, message: 'Please enter referring physician' }]}
          >
            <Input
              placeholder="Enter referring physician name"
              onChange={checkForChanges}
            />
          </Form.Item>
        </div>

        <Form.Item
          label={FieldLabels.HISTORY}
          name="history"
          style={{ marginBottom: 24, marginTop: 4 }}
        >
          <TextArea
            placeholder="Enter patient history"
            rows={4}
            maxLength={500}
            showCount
            onChange={checkForChanges}
          />
        </Form.Item>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={loading}
            disabled={!hasChanges}
          >
            Submit
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default EditStudyDetailsModal;
